<template>
  <label
    :class="[
      { 'is-disabled': isDisabled },
      { 'is-checked': isChecked },
    ]"
    :aria-checked="isChecked"
    :aria-disabled="isDisabled"
    class="el-checkbox"
    role="checkbox"
  >
    <span
      :class="{
        'is-disabled': isDisabled,
        'is-checked': isChecked,
        'is-focus': focus,
      }"
      class="el-checkbox__input"
    >
      <span :class="`size--${checkboxSizeClass}`" class="el-checkbox__inner">
        <i v-if="isChecked" class="ddi ddi-check-thick" />
      </span>

      <input
        :checked="isChecked"
        :disabled="isDisabled"
        class="el-checkbox__original"
        type="checkbox"
        aria-hidden="true"
        @change="handleChange"
        @focus="focus = true"
        @blur="focus = false"
      >
    </span>
    <span v-if="$slots.default" class="el-checkbox__label">
      <slot />
    </span>
  </label>
</template>


<script lang="ts">
import { defineComponent } from 'vue'
import { formContextKey } from 'element-plus'

/**
 * Checkbox with Element's look and a custom check mark and size
 */

export default defineComponent({
  name: 'DdCheckbox',

  inject: {
    elForm: {
      from: formContextKey,
      default: null,
    },
  },

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Checkbox size: normal(12), medium(16) or large(18)
     */
    ddSize: {
      type: String,
      default: 'normal',
    },
  },

  emits: ['change', 'update:modelValue'],

  data() {
    return {
      focus: false,
    }
  },

  computed: {
    isChecked(): boolean {
      return this.modelValue
    },
    isDisabled(): boolean {
      const form = this.elForm as { disabled?: boolean } | null
      return this.disabled || !!(form && form.disabled)
    },
    checkboxSizeClass(): string {
      let size = 'normal'

      switch (this.ddSize) {
        case 'medium':
          size = 'medium'
          break
        case 'large':
          size = 'large'
          break
        default:
          break
      }
      return size
    },
  },

  methods: {
    handleChange(event: Event) {
      const checked = !this.isChecked
      // The prop decides what's shown, so a parent that only listens to change can decline the toggle
      ;(event.target as HTMLInputElement).checked = this.isChecked
      this.$emit('update:modelValue', checked)
      this.$emit('change', checked, event)
    },
  },
})
</script>

<style lang="scss" scoped>
.el-checkbox__input {
  .el-checkbox__original {
    display: none;
  }
}

.el-checkbox__inner {
  display: flex;
  align-items: center;
  justify-content: center;

  .ddi.ddi-check-thick {
    color: var(--color-general-white);
  }

  &.size--normal,
  &.size--medium,
  &.size--large {
    border-radius: 4px;
  }

  &.size--normal {
    width: 12px;
    height: 12px;

    .ddi.ddi-check-thick {
      font-size: 10px;
    }
  }

  &.size--medium {
    width: 16px;
    height: 16px;

    .ddi.ddi-check-thick {
      font-size: 14px;
    }
  }

  &.size--large {
    width: 18px;
    height: 18px;

    .ddi.ddi-check-thick {
      font-size: 16px;
    }
  }
}
</style>
